/* Door planner — plan every opening, build each door set, buy the lot in one action.

   Master–detail: the left rail lists the openings, the right pane builds the selected one.
   Adding or editing an opening is a modal; so is choosing a door, a frame or hardware.

   Where the data comes from
     templates/collection.d2f-data.liquid (?view=d2f-data) and templates/search.d2f-data.liquid
     (/search?view=d2f-data) — the storefront's own facets and search do the work, so prices,
     sizes and availability are Shopify's on every request. The door, frame and hardware
     pickers are the same component pointed at different collections.

   Where the plan lives
     localStorage for "come back tomorrow", and the URL fragment for "send it to my joiner".
     The fragment carries only rooms, measurements, quantities and variant ids; product detail
     is re-fetched, so a shared link is small and can never show a stale price.

   What it deliberately does NOT do
     No automated fit or fire promise. The catalogue records no trim allowance, weight or
     handing, so any such claim would be a guess. Where an opening size is given the planner
     shows the arithmetic and leaves the judgement to the shopper.
*/
(function () {
  var STORE_KEY = 'd2f-door-plan-v2';
  var LEGACY_KEY = 'd2f-door-plan-v1';

  /* A door leaf is never the size of the structural opening: a lining runs down each side plus
     a fitting gap, roughly 60–76mm in total, which is why a 762mm door suits an 838mm opening.
     Used only to order what is shown first; the planner still shows the arithmetic. */
  var LINING_ALLOWANCE_MM = 60;

  /* ---------------------------------------------------------------- utilities */

  function money(pennies) {
    return '£' + (pennies / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function ref(index) {
    return (index + 1 < 10 ? '0' : '') + (index + 1);
  }

  // Built from a string rather than written literally, so no raw control bytes sit in this
  // file (they survive minification badly and make the asset awkward to diff).
  var CONTROL_CHARS = new RegExp('[\\u0000-\\u001F\\u007F]', 'g');

  /* Room names are echoed back by Shopify as line-item properties and the theme's cart
     templates print properties without escaping (stock Dawn). A name from a shared link is
     untrusted, so angle brackets and control characters go before it can reach the basket. */
  function cleanText(value, limit) {
    return String(value == null ? '' : value)
      .replace(/[<>]/g, '')
      .replace(CONTROL_CHARS, ' ')
      .trim()
      .slice(0, limit || 120);
  }

  function measured(o) {
    var h = String(o.oh || '').trim();
    var w = String(o.ow || '').trim();
    if (h && w) return h + ' x ' + w + 'mm';
    if (w) return w + 'mm wide';
    if (h) return h + 'mm high';
    return '';
  }

  // URL-safe base64 over UTF-8, so an accented room name survives the round trip.
  function pack(text) {
    var bytes = new TextEncoder().encode(text);
    var binary = '';
    bytes.forEach(function (b) { binary += String.fromCharCode(b); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function unpack(token) {
    var b64 = token.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return new TextDecoder().decode(Uint8Array.from(atob(b64), function (c) { return c.charCodeAt(0); }));
  }

  function uid() {
    return 'o' + Math.random().toString(36).slice(2, 9);
  }

  var ICONS = {
    door: '<path d="M7 3h10v18H7z"/><path d="M14.2 12h.01" stroke-width="2.6"/><path d="M4 21h16"/>',
    frame: '<path d="M4 3h16v18H4z"/><path d="M8 7h8v14H8z"/>',
    hardware: '<circle cx="9" cy="12" r="2.4"/><path d="M11.4 12H20"/><path d="M17 9.5v5"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>',
    tick: '<path d="M4 12.5l5 5L20 6.5"/>',
    x: '<path d="M6 6l12 12M18 6L6 18"/>',
    ext: '<path d="M14 4h6v6"/><path d="M20 4l-9 9"/><path d="M18 14v6H4V6h6"/>'
  };

  function svg(paths, cls) {
    return '<svg class="' + (cls || '') + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths + '</svg>';
  }

  /* ---------------------------------------------------------------- the planner */

  function Planner(root) {
    var config = {};
    try {
      config = JSON.parse(root.querySelector('[data-d2f-plan-config]').textContent);
    } catch (e) {
      config = {};
    }

    var els = {
      rail: root.querySelector('[data-rail]'),
      railCount: root.querySelector('[data-rail-count]'),
      detail: root.querySelector('[data-detail]'),
      status: root.querySelector('[data-status]'),
      total: root.querySelector('[data-total]'),
      count: root.querySelector('[data-count]'),
      addAll: root.querySelector('[data-add-all]'),
      print: root.querySelector('[data-print]'),
      share: root.querySelector('[data-share]'),
      schedule: root.querySelector('[data-schedule]'),
      openingModal: root.querySelector('[data-opening-modal]'),
      openingForm: root.querySelector('[data-opening-form]'),
      omEyebrow: root.querySelector('[data-om-eyebrow]'),
      omTitle: root.querySelector('[data-om-title]'),
      omSaveLabel: root.querySelector('[data-om-save-label]'),
      pickerModal: root.querySelector('[data-picker-modal]'),
      pkTitle: root.querySelector('[data-pk-title]'),
      pkTabs: root.querySelector('[data-pk-tabs]'),
      pkSearch: root.querySelector('[data-pk-searchinput]'),
      pkMatch: root.querySelector('[data-pk-match]'),
      pkResults: root.querySelector('[data-pk-results]'),
      pkFoot: root.querySelector('[data-pk-foot]'),
      pkFootCount: root.querySelector('[data-pk-foot-count]'),
    };

    var state = { openings: [] };
    var activeId = null;        // opening shown in the detail pane
    var editingId = null;       // opening being edited in the form modal (null = new)
    var picker = null;          // { kind, openingId, tabs, tabIndex, page, multi }
    var submitting = false;
    var searchSeq = 0;
    var searchDebounce = null;
    var pendingSearchTerm = '';   // a typed style has no collection; search for it instead
    var lastFocus = null;

    /* ------------------------------------------------------------ persistence */

    function compact() {
      return state.openings.map(function (o) {
        var out = { r: o.room || '', q: o.qty || 1 };
        if (o.location) out.loc = o.location;
        if (o.ow) out.w = o.ow;
        if (o.oh) out.h = o.oh;
        if (o.thickness) out.t = o.thickness;
        if (o.fire && o.fire !== 'Standard') out.f = o.fire;
        if (o.style) out.s = o.style;
        if (o.door) { out.hd = o.door.handle; out.v = o.door.variantId; }
        if (o.extras && o.extras.length) {
          out.x = o.extras.map(function (e) { return { hd: e.handle, v: e.variantId, q: e.qty, k: e.kind }; });
        }
        return out;
      });
    }

    function save() {
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(compact()));
      } catch (e) { /* private browsing: the plan lives for this page view only */ }
    }

    /* Every field is treated as hostile: a plan can arrive from localStorage or from a link a
       stranger sent. Non-object rows are dropped rather than dereferenced (a null row used to
       throw and blank the page), measurements are reduced to digits, handles must look like
       handles. */
    function expand(rows) {
      if (!Array.isArray(rows)) return [];
      var mm = function (v) { return /^[0-9]{2,5}$/.test(String(v == null ? '' : v)) ? String(v) : ''; };
      var handleOk = function (h) { return typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,120}$/i.test(h); };

      return rows.filter(function (r) {
        return r && typeof r === 'object' && !Array.isArray(r);
      }).slice(0, 60).map(function (r) {
        var variantId = parseInt(r.v, 10);
        var extras = Array.isArray(r.x) ? r.x.filter(function (e) {
          return e && typeof e === 'object' && handleOk(e.hd) && parseInt(e.v, 10) > 0;
        }).slice(0, 30).map(function (e) {
          return {
            handle: e.hd,
            variantId: parseInt(e.v, 10),
            qty: Math.min(999, Math.max(1, parseInt(e.q, 10) || 1)),
            kind: e.k === 'frame' ? 'frame' : 'hardware',
            pending: true,
          };
        }) : [];

        return {
          id: uid(),
          room: typeof r.r === 'string' ? cleanText(r.r) : '',
          location: typeof r.loc === 'string' ? cleanText(r.loc, 60) : '',
          ow: mm(r.w),
          oh: mm(r.h),
          thickness: mm(r.t),
          fire: r.f === 'FD30' || r.f === 'FD60' ? r.f : 'Standard',
          style: typeof r.s === 'string' ? cleanText(r.s, 40) : '',
          qty: Math.min(999, Math.max(1, parseInt(r.q, 10) || 1)),
          door: handleOk(r.hd) && variantId > 0 ? { handle: r.hd, variantId: variantId, pending: true } : null,
          extras: extras,
        };
      });
    }

    function fromProduct(product, variant) {
      var image = variant.featured_image ? variant.featured_image.src : product.featured_image;
      return {
        handle: product.handle,
        productId: product.id,
        variantId: variant.id,
        title: product.title,
        vendor: product.vendor || '',
        size: variant.title,
        sku: variant.sku || '',
        price: variant.price,
        available: variant.available,
        url: '/products/' + product.handle + '?variant=' + variant.id,
        image: image || '',
      };
    }

    // Product detail is never stored, only re-fetched, so a restored plan is priced today.
    function hydrate() {
      var needed = {};
      state.openings.forEach(function (o) {
        if (o.door && o.door.pending) needed[o.door.handle] = true;
        (o.extras || []).forEach(function (e) { if (e.pending) needed[e.handle] = true; });
      });
      var handles = Object.keys(needed);
      if (!handles.length) { renderAll(); return; }

      setStatus('Checking prices…');
      Promise.all(handles.map(function (handle) {
        return fetch(window.Shopify.routes.root + 'products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })).then(function (products) {
        var byHandle = {};
        products.forEach(function (p) { if (p) byHandle[p.handle] = p; });
        var lost = 0;

        function resolve(entry) {
          var product = byHandle[entry.handle];
          var variant = product && product.variants.filter(function (v) { return v.id === entry.variantId; })[0];
          if (!variant) return null;
          var out = fromProduct(product, variant);
          out.qty = entry.qty;
          out.kind = entry.kind;
          return out;
        }

        state.openings.forEach(function (o) {
          if (o.door && o.door.pending) {
            var d = resolve(o.door);
            if (!d) { o.door = null; lost += 1; } else { o.door = d; }
          }
          o.extras = (o.extras || []).map(function (e) {
            if (!e.pending) return e;
            var x = resolve(e);
            if (!x) { lost += 1; return null; }
            return x;
          }).filter(Boolean);
        });

        if (lost) save();
        setStatus(lost
          ? (lost === 1 ? 'One item in your plan is no longer available, so it has been removed.'
                        : lost + ' items in your plan are no longer available, so they have been removed.')
          : '', lost > 0);
        renderAll();
      });
    }

    /* ------------------------------------------------------------ totals */

    function openingTotal(o) {
      var sum = 0;
      if (o.door && !o.door.pending && o.door.available !== false) sum += o.door.price * o.qty;
      (o.extras || []).forEach(function (e) {
        if (!e.pending && e.available !== false) sum += e.price * e.qty;
      });
      return sum;
    }

    function totals() {
      var pennies = 0, units = 0, ready = 0;
      state.openings.forEach(function (o) {
        pennies += openingTotal(o);
        if (o.door && !o.door.pending && o.door.available !== false) { ready += 1; units += o.qty; }
        (o.extras || []).forEach(function (e) { if (!e.pending && e.available !== false) units += e.qty; });
      });
      return { pennies: pennies, units: units, ready: ready, rooms: state.openings.length };
    }

    /* ------------------------------------------------------------ render */

    function renderAll() {
      renderRail();
      renderDetail();
      renderFoot();
      renderSchedule();
      if (window.d2fApplyVat) window.d2fApplyVat(root);
    }

    function renderRail() {
      var t = totals();
      els.railCount.textContent = t.rooms === 0
        ? 'No doors planned yet'
        : t.rooms === 1 ? '1 door planned' : t.rooms + ' doors planned';

      els.rail.innerHTML = state.openings.map(function (o, i) {
        var bits = [];
        if (o.ow && o.oh) bits.push(o.ow + ' × ' + o.oh);
        else if (o.ow) bits.push(o.ow + 'mm wide');
        else if (o.oh) bits.push(o.oh + 'mm high');
        if (o.thickness) bits.push(o.thickness + ' mm');
        var ready = !!(o.door && !o.door.pending);
        return '<button type="button" class="d2f-plan__rail-item' + (o.id === activeId ? ' is-active' : '') + '" data-select="' + o.id + '">' +
            '<span class="d2f-plan__rail-num">' + ref(i) + '</span>' +
            '<span class="d2f-plan__rail-body">' +
              '<span class="d2f-plan__rail-room">' + esc(o.room || 'Opening ' + ref(i)) + '</span>' +
              '<span class="d2f-plan__rail-meta">' +
                '<span class="d2f-plan__rail-dot' + (ready ? ' is-ready' : '') + '"></span>' +
                esc(bits.join(' · ') || 'No measurements yet') +
              '</span>' +
            '</span>' +
          '</button>' +
          '<span class="d2f-plan__rail-remove" role="button" tabindex="0" data-remove="' + o.id + '" aria-label="Remove ' + esc(o.room || 'opening ' + ref(i)) + '">' +
            svg(ICONS.x) +
          '</span>';
      }).join('');

      // The remove control is a sibling so it is not a button inside a button; re-attach it
      // visually to each row by wrapping pairs in a positioned container.
      var nodes = Array.prototype.slice.call(els.rail.children);
      els.rail.innerHTML = '';
      for (var i = 0; i < nodes.length; i += 2) {
        var wrap = document.createElement('div');
        wrap.style.position = 'relative';
        wrap.appendChild(nodes[i]);
        if (nodes[i + 1]) wrap.appendChild(nodes[i + 1]);
        els.rail.appendChild(wrap);
      }
    }

    function renderDetail() {
      var o = byId(activeId);
      if (!o) {
        els.detail.innerHTML =
          '<div class="d2f-plan__empty-detail">' +
            svg(ICONS.door) +
            '<h2>Start with your first opening</h2>' +
            '<p>Tell us the room and, if you know them, the measurements. We will show doors that suit it, with live prices and stock.</p>' +
            '<button type="button" class="d2f-plan__btn d2f-plan__btn--gold" data-open-new>Add an opening</button>' +
          '</div>';
        return;
      }

      var index = state.openings.indexOf(o);
      var stat = function (label, value, unit) {
        return '<div class="d2f-dt__stat"><span class="d2f-dt__stat-label">' + label + '</span>' +
          '<span class="d2f-dt__stat-value">' + esc(value) + (unit ? ' <small>' + unit + '</small>' : '') + '</span></div>';
      };

      var doorBlock;
      if (o.door && !o.door.pending) {
        var d = o.door;
        doorBlock =
          '<div class="d2f-dt__door"><div class="d2f-dt__chosen">' +
            (d.image ? '<img class="d2f-dt__chosen-img" src="' + esc(d.image) + '" alt="" loading="lazy">' : '') +
            '<div class="d2f-dt__chosen-body">' +
              (d.vendor ? '<span class="d2f-dt__chosen-brand">' + esc(d.vendor) + '</span>' : '') +
              '<span class="d2f-dt__chosen-title"><a href="' + esc(d.url) + '" target="_blank" rel="noopener">' + esc(d.title) + '</a></span>' +
              '<span class="d2f-dt__chosen-meta">' + esc(d.size) + (d.sku ? ' · ' + esc(d.sku) : '') + '</span>' +
              (d.available === false ? '<span class="d2f-dt__chosen-warn">Currently unavailable — please choose another</span>' : '') +
              (gapHint(o) ? '<span class="d2f-dt__chosen-meta">' + gapHint(o) + '</span>' : '') +
              '<div class="d2f-dt__chosen-foot">' +
                qtyHtml(o.id, 'door', o.qty) +
                '<span class="d2f-dt__chosen-price d2f-price">' + money(d.price * o.qty) + '</span>' +
                '<button type="button" class="d2f-plan__btn d2f-plan__btn--ghost" data-pick="door" data-for="' + o.id + '">Change door</button>' +
              '</div>' +
            '</div>' +
          '</div></div>';
      } else {
        doorBlock =
          '<div class="d2f-dt__door d2f-dt__door--empty">' +
            '<div class="d2f-dt__door-ph">' + svg(ICONS.door) + '</div>' +
            '<div class="d2f-dt__door-empty-body">' +
              '<span class="d2f-dt__door-eyebrow">No door selected</span>' +
              '<div class="d2f-dt__door-h">Find a door for this opening</div>' +
              '<p class="d2f-dt__door-p">Search genuine products and choose the exact size variant.</p>' +
            '</div>' +
            '<button type="button" class="d2f-plan__btn d2f-plan__btn--gold" data-pick="door" data-for="' + o.id + '">Find live doors</button>' +
          '</div>';
      }

      var frames = (o.extras || []).filter(function (e) { return e.kind === 'frame'; });
      var hardware = (o.extras || []).filter(function (e) { return e.kind === 'hardware'; });

      var tile = function (kind, icon, title, sub, action) {
        return '<button type="button" class="d2f-dt__tile" data-pick="' + kind + '" data-for="' + o.id + '">' +
          '<span class="d2f-dt__tile-ic">' + svg(icon) + '</span>' +
          '<span class="d2f-dt__tile-body">' +
            '<span class="d2f-dt__tile-title">' + title + '</span>' +
            '<span class="d2f-dt__tile-sub">' + sub + '</span>' +
          '</span>' +
          '<span class="d2f-dt__tile-act">' + action + '</span>' +
        '</button>';
      };

      els.detail.innerHTML =
        '<div class="d2f-dt__head">' +
          '<div>' +
            '<span class="d2f-dt__eyebrow">Opening ' + ref(index) + '</span>' +
            '<h2 class="d2f-dt__room">' + esc(o.room || 'Opening ' + ref(index)) + '</h2>' +
            (o.location || o.style
              ? '<span class="d2f-dt__loc">' + esc(o.location || '') +
                (o.style ? (o.location ? ' ' : '') + '<span class="d2f-dt__style">' + esc(o.style) + '</span>' : '') +
                '</span>'
              : '') +
          '</div>' +
          '<button type="button" class="d2f-dt__edit" data-edit="' + o.id + '">Edit opening</button>' +
        '</div>' +

        '<div class="d2f-dt__stats">' +
          stat('Width', o.ow || '—', o.ow ? 'mm' : '') +
          stat('Height', o.oh || '—', o.oh ? 'mm' : '') +
          stat('Thickness', o.thickness || '—', o.thickness ? 'mm' : '') +
          stat('Fire rating', o.fire || 'Standard', '') +
        '</div>' +

        '<div class="d2f-dt__cat">' +
          '<span class="d2f-dt__cat-ic">' + svg(ICONS.search) + '</span>' +
          '<span class="d2f-dt__cat-body">' +
            '<span class="d2f-dt__cat-title">Choose from the live Doors2Floors catalogue</span>' +
            '<span class="d2f-dt__cat-sub">We show exact-size variants first, with current prices and availability.</span>' +
          '</span>' +
          '<button type="button" class="d2f-dt__cat-link" data-pick="door" data-for="' + o.id + '">Find live products</button>' +
        '</div>' +

        '<div class="d2f-dt__upsell">' +
          '<span class="d2f-dt__upsell-eyebrow">Complete this door</span>' +
          '<div class="d2f-dt__upsell-h">Build the room from real store products</div>' +
          '<div class="d2f-dt__tiles">' +
            tile('door', ICONS.door, 'Choose Door', 'Internal, fire and external doors', o.door && !o.door.pending ? 'Change' : 'Choose') +
            tile('frame', ICONS.frame, 'Add woodwork', 'Browse frames, skirtings and architraves', frames.length ? 'Add more' : 'Add') +
            tile('hardware', ICONS.hardware, 'Add hardware', 'Handles, hinges, locks and fitting packs', hardware.length ? 'Add more' : 'Add') +
          '</div>' +
        '</div>' +

        doorBlock +

        (o.extras && o.extras.length ? '<ul class="d2f-dt__added">' + o.extras.map(function (e) {
            return '<li class="d2f-dt__added-item">' +
              (e.image ? '<img class="d2f-dt__added-img" src="' + esc(e.image) + '" alt="" loading="lazy">' : '') +
              '<span class="d2f-dt__added-body">' +
                '<span class="d2f-dt__added-tag">' + (e.kind === 'frame' ? 'Woodwork' : 'Hardware') + '</span>' +
                '<span class="d2f-dt__added-title">' + esc(e.title) + '</span>' +
                '<span class="d2f-dt__added-tag">' + esc(e.size !== 'Default Title' ? e.size : (e.sku || '')) + '</span>' +
                (e.available === false ? '<span class="d2f-dt__chosen-warn">Currently unavailable</span>' : '') +
              '</span>' +
              qtyHtml(o.id, e.variantId, e.qty) +
              '<span class="d2f-dt__added-price d2f-price">' + money(e.price * e.qty) + '</span>' +
              '<button type="button" class="d2f-dt__added-remove" data-drop="' + e.variantId + '" data-for="' + o.id + '" aria-label="Remove ' + esc(e.title) + '">' + svg(ICONS.x) + '</button>' +
            '</li>';
          }).join('') + '</ul>' : '');
    }

    function qtyHtml(openingId, target, qty) {
      return '<span class="d2f-qty">' +
        '<button type="button" data-step="-1" data-for="' + openingId + '" data-target="' + target + '" aria-label="Fewer">&minus;</button>' +
        '<input type="number" min="1" step="1" value="' + qty + '" inputmode="numeric" data-qty data-for="' + openingId + '" data-target="' + target + '" aria-label="Quantity">' +
        '<button type="button" data-step="1" data-for="' + openingId + '" data-target="' + target + '" aria-label="More">+</button>' +
      '</span>';
    }

    /* Arithmetic, not advice: what is left once the leaf comes out of the opening. Uses the
       PARSED number, never the raw field — this string is written with innerHTML and ow can
       arrive from a shared link. */
    function gapHint(o) {
      if (!o.door || o.door.pending || !o.ow) return '';
      var figures = String(o.door.size).match(/(\d{3,4})\s*mm/g);
      if (!figures || figures.length < 2) return '';
      var leaf = parseInt(figures[1], 10);
      var openingW = parseInt(o.ow, 10);
      var gap = openingW - leaf;
      if (isNaN(gap) || isNaN(leaf) || isNaN(openingW)) return '';
      if (gap < 0) return '<b>This door is wider than the opening you measured.</b>';
      return 'Your ' + openingW + 'mm opening, less the ' + leaf + 'mm door, leaves <b>' + gap +
        'mm</b> for the lining and fitting gap (' + Math.round(gap / 2) + 'mm a side).';
    }

    function renderFoot() {
      var t = totals();
      els.total.textContent = money(t.pennies);
      delete els.total.dataset.d2fInc;
      els.count.textContent = t.units ? '(' + t.units + (t.units === 1 ? ' item)' : ' items)') : '';
      // Never re-enable mid-flight: a re-render during the request used to switch the button
      // back on and let the whole plan be added twice.
      els.addAll.disabled = submitting || t.units === 0;
      els.print.disabled = t.ready === 0;
      els.share.disabled = t.ready === 0;
    }

    function renderSchedule() {
      var t = totals();
      var rows = '';
      state.openings.forEach(function (o, i) {
        var openingSize = measured(o) || '—';
        if (o.door && !o.door.pending && o.door.available !== false) {
          rows += scheduleRow(ref(i), o.room, 'Door', o.door, openingSize, o.qty);
        }
        (o.extras || []).forEach(function (e) {
          if (e.pending || e.available === false) return;
          rows += scheduleRow(ref(i), o.room, e.kind === 'frame' ? 'Woodwork' : 'Hardware', e, openingSize, e.qty);
        });
      });

      var today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      els.schedule.innerHTML =
        '<div class="d2f-plan__sched-title">Door schedule</div>' +
        '<div class="d2f-plan__sched-meta">Doors2Floors · ' + today + ' · ' + t.rooms +
          ' opening' + (t.rooms === 1 ? '' : 's') + ', ' + t.units + ' item' + (t.units === 1 ? '' : 's') + '</div>' +
        '<table><thead><tr>' +
          '<th>Ref</th><th>Room / opening</th><th>Type</th><th>Product</th><th>Code</th><th>Option</th>' +
          '<th>Measured opening</th><th style="text-align:right">Qty</th><th style="text-align:right">Price</th>' +
        '</tr></thead><tbody>' + rows + '</tbody>' +
        '<tfoot><tr><td colspan="8">Total (inc. VAT)</td><td style="text-align:right">' + money(t.pennies) + '</td></tr></tfoot></table>' +
        '<div class="d2f-plan__sched-foot">' +
          'Door sizes shown are the door leaf, not the structural opening. Allow for your lining and fitting gap. ' +
          'Trimming allowance varies by door and is not confirmed here — please check before cutting. ' +
          'Prices were correct on ' + today + ' and are confirmed at checkout.<br>' +
          'Doors2Floors, 205 Bradford Road, Batley, West Yorkshire, WF13 2HD · 01924 457457 · sales@doors2floors.co.uk' +
        '</div>';
    }

    function scheduleRow(r, room, type, item, openingSize, qty) {
      return '<tr>' +
        '<td>' + r + '</td>' +
        '<td>' + esc(room || 'Unnamed opening') + '</td>' +
        '<td>' + type + '</td>' +
        '<td>' + esc(item.title) + '</td>' +
        '<td>' + esc(item.sku) + '</td>' +
        '<td>' + esc(item.size !== 'Default Title' ? item.size : '') + '</td>' +
        '<td>' + esc(openingSize) + '</td>' +
        '<td style="text-align:right">' + qty + '</td>' +
        '<td style="text-align:right">' + money(item.price * qty) + '</td>' +
      '</tr>';
    }

    function setStatus(text, warn) {
      els.status.textContent = text || '';
      els.status.classList.toggle('d2f-plan__status--warn', !!warn);
    }

    function byId(id) {
      return state.openings.filter(function (o) { return o.id === id; })[0];
    }

    /* ------------------------------------------------------------ modals */

    function openModal(modal) {
      lastFocus = document.activeElement;
      modal.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
    }

    function closeModals() {
      root.querySelectorAll('.d2f-modal[open]').forEach(function (m) { m.removeAttribute('open'); });
      document.body.style.overflow = '';
      picker = null;
      if (lastFocus && document.contains(lastFocus)) {
        try { lastFocus.focus({ preventScroll: true }); } catch (e) {}
      }
      lastFocus = null;
    }

    var OTHER = '__other';

    /* A select whose list cannot be exhaustive is paired with a text box. These two helpers
       keep the pair in step: setField puts a stored value back (choosing "Other" and filling
       the box when the value is not one of the listed options), readField gives back whatever
       the shopper actually meant. */
    function otherInput(select) {
      return select && select.dataset.other ? els.openingForm.elements[select.dataset.other] : null;
    }

    function syncOther(select) {
      var box = otherInput(select);
      if (!box) return;
      var show = select.value === OTHER;
      box.hidden = !show;
      if (!show) box.value = '';
    }

    function setField(select, value) {
      var box = otherInput(select);
      var listed = Array.prototype.some.call(select.options, function (o) { return o.value === value; });
      if (value && !listed && box) {
        select.value = OTHER;
        box.value = value;
      } else {
        select.value = value || select.options[0].value;
      }
      syncOther(select);
      if (select.value === OTHER && box) box.value = value || '';
    }

    function readField(select, limit) {
      var box = otherInput(select);
      if (select.value === OTHER) return box ? cleanText(box.value, limit) : '';
      return cleanText(select.value, limit);
    }

    function openOpeningModal(id) {
      editingId = id || null;
      var o = id ? byId(id) : null;
      var f = els.openingForm;
      f.room.value = o ? o.room : '';
      f.ow.value = o ? o.ow : '';
      f.oh.value = o ? o.oh : '';
      f.fire.value = o && o.fire ? o.fire : 'Standard';
      setField(f.location, o ? o.location : '');
      setField(f.thickness, o ? o.thickness : '');
      setField(f.style, o ? o.style : '');

      els.omEyebrow.textContent = o ? 'Edit opening' : 'New opening';
      els.omTitle.textContent = o ? 'Update this opening' : 'Tell us where this door is going';
      els.omSaveLabel.textContent = o ? 'Save changes' : 'Save opening';

      openModal(els.openingModal);
      setTimeout(function () { f.room.focus({ preventScroll: true }); }, 60);
    }

    els.openingForm.addEventListener('submit', function (event) {
      event.preventDefault();
      var f = els.openingForm;
      var mm = function (v) { return /^[0-9]{2,5}$/.test(String(v || '').trim()) ? String(v).trim() : ''; };
      var data = {
        room: cleanText(f.room.value) || 'Opening',
        location: readField(f.location, 60),
        ow: mm(f.ow.value),
        oh: mm(f.oh.value),
        // a typed thickness is still a measurement, so it goes through the same digits-only gate
        thickness: mm(readField(f.thickness, 5)),
        fire: f.fire.value === 'FD30' || f.fire.value === 'FD60' ? f.fire.value : 'Standard',
        style: readField(f.style, 40),
      };

      if (editingId) {
        var o = byId(editingId);
        if (o) Object.keys(data).forEach(function (k) { o[k] = data[k]; });
        activeId = editingId;
      } else {
        var created = {
          id: uid(), qty: 1, door: null, extras: [],
          room: data.room, location: data.location, ow: data.ow, oh: data.oh,
          thickness: data.thickness, fire: data.fire, style: data.style,
        };
        state.openings.push(created);
        activeId = created.id;
      }
      editingId = null;
      save();
      closeModals();
      renderAll();
      setStatus('Opening saved.');
    });

    /* ------------------------------------------------------------ picker */

    var KINDS = {
      door: { title: 'Choose a live store door', tabsKey: 'doorTabs', multi: false, noun: 'door' },
      frame: { title: 'Choose live store woodwork', tabsKey: 'frameTabs', multi: true, noun: 'woodwork' },
      hardware: { title: 'Add hardware to this door', tabsKey: 'hardwareTabs', multi: true, noun: 'hardware' },
    };

    function openPicker(kind, openingId) {
      var spec = KINDS[kind];
      if (!spec) return;
      var tabs = (config[spec.tabsKey] || []).slice();
      var o = byId(openingId);
      var startTab = 0;

      if (kind === 'door' && o) {
        var wantsFire = o.fire === 'FD30' || o.fire === 'FD60';
        var sc = o.style && (config.styleCollections || {})[o.style];

        /* A preferred style is a promise. If the shopper said Oak, every tab in front of them
           should be oak — otherwise one click on "Internal doors" puts white doors back in
           view, which is exactly what it looked like before. Style has no storefront filter
           (the colour metafield is empty catalogue-wide), so it selects collections instead.
           The general tabs stay, relabelled "All …", so widening is still possible but is a
           deliberate act rather than the default. With no style chosen, nothing changes. */
        if (sc && sc.collection) {
          var styleTabs = [{
            label: o.style + ' doors',
            collection: sc.collection,
            search: o.style + ' doors',
            isStyle: true,
          }];
          if (sc.fire) {
            styleTabs.push({
              label: o.style + ' fire doors',
              collection: sc.fire,
              search: o.style + ' fire doors',
              isStyle: true,
            });
          }
          var widen = tabs.map(function (t) {
            return { label: 'All ' + t.label.toLowerCase(), collection: t.collection, search: t.search };
          });
          tabs = styleTabs.concat(widen);

          if (wantsFire) {
            // Not every style has a fire range (Laminated and Pine do not); fall back to the
            // full fire-door list rather than silently showing non-fire doors.
            startTab = sc.fire
              ? 1
              : tabs.map(function (t, i) { return /fire/i.test(t.label) ? i : -1; })
                  .filter(function (i) { return i > -1; })[0] || 0;
          }
        } else if (wantsFire) {
          var fireTab = tabs.map(function (t, i) { return /fire/i.test(t.label) ? i : -1; })
            .filter(function (i) { return i > -1; })[0];
          if (fireTab != null) startTab = fireTab;
        }

        // A style typed into "Other" has no collection of ours. Rather than ignore it, seed
        // the search box with it so the first thing shown is at least relevant.
        if (o.style && !sc) pendingSearchTerm = o.style + ' door';
      }

      picker = { kind: kind, openingId: openingId, tabs: tabs, tabIndex: startTab, page: 1, multi: spec.multi, added: 0 };

      els.pkTitle.textContent = spec.title;
      els.pkSearch.value = pendingSearchTerm || '';
      pendingSearchTerm = '';
      els.pkSearch.placeholder = 'Search ' + (tabs[picker.tabIndex] ? tabs[picker.tabIndex].search : 'the catalogue') + '…';
      els.pkFoot.hidden = false;
      updateFootCount();
      renderTabs();
      renderMatchBar();
      openModal(els.pickerModal);
      search(1);
    }

    function renderTabs() {
      els.pkTabs.innerHTML = picker.tabs.map(function (t, i) {
        return '<button type="button" class="d2f-pk__tab' + (i === picker.tabIndex ? ' is-active' : '') + '" data-tab="' + i + '">' + esc(t.label) + '</button>';
      }).join('');
    }

    function renderMatchBar() {
      var o = byId(picker.openingId);
      if (!o || (!o.ow && !o.oh && !o.thickness && !o.style)) { els.pkMatch.hidden = true; return; }
      var bits = [];
      if (o.oh) bits.push(o.oh);
      if (o.ow) bits.push(o.ow);
      var size = bits.join(' × ');
      var extra = [];
      if (o.thickness) extra.push(o.thickness + ' mm');
      if (o.fire) extra.push(o.fire);
      if (o.style) extra.push(o.style);
      els.pkMatch.hidden = false;
      els.pkMatch.innerHTML =
        '<span class="d2f-pk__match-ic">' + svg('<path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/><path d="M14 6l4 4"/>') + '</span>' +
        '<span><b>Matching for ' + esc(o.room || 'this opening') + '</b> ' + esc(size ? size + ' mm' : 'no size given') +
        (extra.length ? ' · ' + esc(extra.join(' · ')) : '') + '</span>' +
        '<span class="d2f-pk__match-spacer">Exact-size options are shown first</span>';
    }

    function buildUrl(page) {
      var term = els.pkSearch.value.trim();
      var params = new URLSearchParams();
      params.set('view', 'd2f-data');

      if (term) {
        // Same rule as the site's search box (d2f-search-strict.js): Shopify matches ANY word,
        // so "Victorian door" returns everything containing "door". Joining with AND makes a
        // multi-word search mean all of its words, unless the shopper is using operators.
        var words = term.split(/\s+/).filter(Boolean);
        var hasOps = words.some(function (w) {
          return /^(AND|OR|NOT)$/i.test(w) || w.charAt(0) === '-' || w.indexOf('"') > -1;
        });
        params.set('q', words.length > 1 && !hasOps ? words.join(' AND ') : term);
        params.set('type', 'product');
        if (page > 1) params.set('page', page);
        return window.Shopify.routes.root + 'search?' + params.toString();
      }

      var tab = picker.tabs[picker.tabIndex];
      var base = (tab && tab.collection) || config.defaultCollection || '/collections/all';
      var o = byId(picker.openingId);
      // Doors are the only thing with reliable size metafields, so only they get size facets.
      if (picker.kind === 'door' && o) {
        if (o.oh) params.append('filter.v.m.custom.height_mm', o.oh);
        if (o.ow) params.append('filter.v.m.custom.width_mm', o.ow);
      }
      if (page > 1) params.set('page', page);
      return base + '?' + params.toString();
    }

    function search(page, append) {
      var url = buildUrl(page);
      var seq = ++searchSeq;
      picker.page = page;

      if (append) {
        var btn = els.pkResults.querySelector('[data-more]');
        if (btn) { btn.disabled = true; btn.textContent = 'Loading…'; }
      } else {
        els.pkResults.innerHTML = '<div class="d2f-pk__loading">Loading live products…</div>';
      }

      function fail() {
        if (seq !== searchSeq) return;
        if (append) {
          var b = els.pkResults.querySelector('[data-more]');
          if (b) { b.disabled = false; b.textContent = 'Show more'; }
          return;
        }
        els.pkResults.innerHTML = '<div class="d2f-pk__none">Sorry, we could not load products just now. Please try again.</div>';
      }

      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (text) {
          // Two quick filter changes fire two requests; without this the slower could land
          // last and paint results for a search the shopper has moved on from.
          if (seq !== searchSeq) return;
          var data;
          try { data = JSON.parse(text); } catch (e) { fail(); return; }
          renderResults(data, page, append);
        })
        .catch(fail);
    }

    function renderResults(data, page, append) {
      var o = byId(picker.openingId);
      var wantW = o && o.ow ? parseInt(o.ow, 10) : null;
      var wantH = o && o.oh ? parseInt(o.oh, 10) : null;

      if (!data.products || !data.products.length) {
        els.pkResults.innerHTML = '<div class="d2f-pk__none">No products match. Try another tab, or clear the search.</div>';
        return;
      }

      var cards = data.products.map(function (p) { return cardHtml(p, wantW, wantH); }).join('');
      var more = page < data.pages
        ? '<div class="d2f-pk__more"><button type="button" class="d2f-plan__btn d2f-plan__btn--ghost" data-more="' + (page + 1) + '">Show more</button></div>'
        : '';

      // Append rather than replace, or "Show more" throws away everything already scrolled.
      if (append) {
        var grid = els.pkResults.querySelector('.d2f-pk__grid');
        var moreWrap = els.pkResults.querySelector('.d2f-pk__more');
        if (grid) grid.insertAdjacentHTML('beforeend', cards);
        if (moreWrap) moreWrap.outerHTML = more;
        if (window.d2fApplyVat) window.d2fApplyVat(els.pkResults);
        return;
      }

      els.pkResults.innerHTML =
        '<p class="d2f-pk__count"><span><b>' + data.total + '</b> live product' + (data.total === 1 ? '' : 's') + ' loaded</span>' +
        '<span>Prices include VAT</span></p>' +
        '<div class="d2f-pk__grid">' + cards + '</div>' + more;

      if (window.d2fApplyVat) window.d2fApplyVat(els.pkResults);
    }

    function cardHtml(p, wantW, wantH) {
      var variants = p.variants || [];
      if (!variants.length) return '';

      // Exact-size variants first, so the option box opens on what the shopper asked for.
      var scored = variants.map(function (v) {
        var exact = (wantW && String(v.width) === String(wantW) ? 1 : 0) + (wantH && String(v.height) === String(wantH) ? 1 : 0);
        return { v: v, exact: exact };
      }).sort(function (a, b) { return b.exact - a.exact; });

      var best = scored[0];
      var hasExact = best.exact > 0 && (wantW || wantH);
      var inStock = variants.some(function (v) { return v.available; });
      var already = isAdded(p.handle);

      var options = scored.map(function (s) {
        return '<option value="' + s.v.id + '" data-price="' + s.v.price + '"' + (s.v.available ? '' : ' disabled') + '>' +
          esc(s.v.title) + (s.v.available ? '' : ' — sold out') + '</option>';
      }).join('');

      return '<div class="d2f-pk__card" data-card="' + esc(p.handle) + '">' +
        '<div class="d2f-pk__card-media">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '') +
          '<span class="d2f-pk__avail' + (inStock ? '' : ' d2f-pk__avail--out') + '">' + (inStock ? 'Available' : 'Sold out') + '</span>' +
        '</div>' +
        '<div class="d2f-pk__card-body">' +
          (p.vendor ? '<span class="d2f-pk__brand">' + esc(p.vendor) + '</span>' : '') +
          '<span class="d2f-pk__name">' + esc(p.title) + '</span>' +
          (variants.length > 1 || variants[0].title !== 'Default Title'
            ? '<span class="d2f-pk__optlabel">Choose exact option</span>' +
              '<select class="d2f-pk__select" data-variant>' + options + '</select>'
            : '<input type="hidden" data-variant value="' + variants[0].id + '">') +
          '<span class="d2f-pk__price"><span class="d2f-price" data-price-display>' + money(best.v.price) + '</span> <small>inc VAT</small>' +
            (best.v.sku ? ' <small>· SKU ' + esc(best.v.sku) + '</small>' : '') + '</span>' +
          (hasExact ? '<span class="d2f-pk__matchflag">' + svg(ICONS.tick) + 'Exact saved size</span>' : '') +
          '<div class="d2f-pk__card-actions">' +
            '<button type="button" class="d2f-pk__use' + (already ? ' is-added' : '') + '" data-use="' + esc(p.handle) + '"' + (inStock ? '' : ' disabled') + '>' +
              (already ? 'Added ✓' : 'Add Product') +
            '</button>' +
            '<a class="d2f-pk__details" href="' + esc(p.url) + '" target="_blank" rel="noopener">Product details' + svg(ICONS.ext) + '</a>' +
          '</div>' +
        '</div>' +
      '</div>';
    }

    function isAdded(handle) {
      var o = byId(picker && picker.openingId);
      if (!o) return false;
      if (picker.kind === 'door') return !!(o.door && o.door.handle === handle);
      return (o.extras || []).some(function (e) { return e.handle === handle; });
    }

    function updateFootCount() {
      if (!picker) return;
      var o = byId(picker.openingId);
      if (picker.kind === 'door') {
        els.pkFootCount.textContent = o && o.door && !o.door.pending
          ? 'Door selected' : 'No door selected yet';
        return;
      }
      var kindKey = picker.kind === 'frame' ? 'frame' : 'hardware';
      var n = o ? (o.extras || []).filter(function (e) { return e.kind === kindKey; }).length : 0;
      els.pkFootCount.textContent = n === 0 ? 'Nothing added yet' : n === 1 ? '1 item added' : n + ' items added';
    }

    function choose(handle, variantId) {
      var o = byId(picker.openingId);
      if (!o) return closeModals();
      var kind = picker.kind;
      var multi = picker.multi;

      fetch(window.Shopify.routes.root + 'products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          var variant = product.variants.filter(function (v) { return v.id === variantId; })[0];
          if (!variant) return;
          var item = fromProduct(product, variant);

          if (kind === 'door') {
            o.door = item;
            save(); renderAll();
            // Same rhythm as hardware: the picker stays open and Done closes it. One door
            // per opening, so the previous card's badge clears when a new one is chosen.
            els.pkResults.querySelectorAll('[data-use].is-added').forEach(function (b) {
              b.classList.remove('is-added');
              b.textContent = 'Add Product';
            });
            var doorBtn = els.pkResults.querySelector('[data-use="' + handle + '"]');
            if (doorBtn) { doorBtn.classList.add('is-added'); doorBtn.textContent = 'Added ✓'; }
            updateFootCount();
            setStatus('Door added to ' + (o.room || 'this opening') + '.');
            return;
          }

          item.kind = kind === 'frame' ? 'frame' : 'hardware';
          item.qty = 1;
          o.extras = o.extras || [];
          var existing = o.extras.filter(function (e) { return e.variantId === variant.id; })[0];
          if (existing) existing.qty += 1;
          else if (kind === 'frame' && false) {   // woodwork is additive now; kept for shape
            // One frame per opening: replace rather than stack.
            o.extras = o.extras.filter(function (e) { return e.kind !== 'frame'; }).concat([item]);
          } else {
            o.extras.push(item);
          }

          save();
          renderRail(); renderDetail(); renderFoot(); renderSchedule();
          if (window.d2fApplyVat) window.d2fApplyVat(root);

          if (multi) {
            // Accessories are chosen several at a time, so the picker stays open.
            picker.added += 1;
            updateFootCount();
            var btn = els.pkResults.querySelector('[data-use="' + handle + '"]');
            if (btn) { btn.classList.add('is-added'); btn.textContent = 'Added ✓'; }
            setStatus(item.title + ' added.');
          } else {
            closeModals();
            setStatus('Frame added to ' + (o.room || 'this opening') + '.');
          }
        })
        .catch(function () { setStatus('Sorry, that could not be added. Please try again.', true); });
    }

    /* ------------------------------------------------------------ basket */

    function addAll() {
      var t = totals();
      if (!t.units) return;

      submitting = true;
      els.addAll.disabled = true;
      setStatus('Checking today’s prices…');

      var handles = {};
      state.openings.forEach(function (o) {
        if (o.door && !o.door.pending) handles[o.door.handle] = true;
        (o.extras || []).forEach(function (e) { if (!e.pending) handles[e.handle] = true; });
      });

      Promise.all(Object.keys(handles).map(function (handle) {
        return fetch(window.Shopify.routes.root + 'products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })).then(function (products) {
        var byHandle = {};
        products.forEach(function (p) { if (p) byHandle[p.handle] = p; });

        var items = [], changed = [], gone = [];

        function push(o, i, entry, type) {
          var product = byHandle[entry.handle];
          var variant = product && product.variants.filter(function (v) { return v.id === entry.variantId; })[0];
          if (!variant || !variant.available) {
            gone.push(ref(i) + ' ' + type);
            entry.available = false;
            return;
          }
          entry.available = true;
          if (variant.price !== entry.price) {
            changed.push(ref(i) + ' ' + type + ': ' + money(entry.price) + ' → ' + money(variant.price));
            entry.price = variant.price;
          }
          var properties = { 'Room': cleanText(o.room) || 'Opening ' + ref(i), 'Opening': ref(i), 'Part': type };
          var size = measured(o);
          if (size) properties['Measured opening'] = size;
          if (o.location) properties['Location'] = cleanText(o.location, 60);
          items.push({ id: variant.id, quantity: entry.qty, properties: properties });
        }

        state.openings.forEach(function (o, i) {
          if (o.door && !o.door.pending) { o.door.qty = o.qty; push(o, i, o.door, 'Door'); }
          (o.extras || []).forEach(function (e) {
            if (!e.pending) push(o, i, e, e.kind === 'frame' ? 'Woodwork' : 'Hardware');
          });
        });

        if (changed.length || gone.length) renderAll();

        if (!items.length) {
          submitting = false;
          els.addAll.disabled = false;
          setStatus('None of those products is available right now. Please choose another.', true);
          return;
        }

        // Shopify keeps lines with different properties separate — exactly what per-room
        // labelling needs: the same door for two rooms arrives as two labelled lines.
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items }),
        })
          .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
          .then(function () {
            submitting = false;
            els.addAll.disabled = false;
            var notes = [];
            if (changed.length) notes.push('Prices updated: ' + changed.join('; ') + '.');
            if (gone.length) notes.push('Not added (unavailable): ' + gone.join('; ') + '.');
            setStatus(items.length + ' item' + (items.length === 1 ? '' : 's') + ' added to your basket. ' + notes.join(' '), gone.length > 0);
            refreshCartCount();
          })
          .catch(function () {
            submitting = false;
            els.addAll.disabled = false;
            setStatus('Sorry, that did not go through. Please try again.', true);
          });
      });
    }

    function refreshCartCount() {
      fetch(window.Shopify.routes.root + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (b) {
            b.textContent = cart.item_count;
          });
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(function () {});
    }

    /* ------------------------------------------------------------ share */

    function shareLink() {
      var url = window.location.origin + window.location.pathname + '#plan=' + pack(JSON.stringify(compact()));
      function done() { setStatus('Link copied. Send it to yourself or your joiner to open this plan anywhere.'); }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, function () { window.prompt('Copy your plan link:', url); });
      } else {
        window.prompt('Copy your plan link:', url);
      }
    }

    /* ------------------------------------------------------------ events */

    root.addEventListener('click', function (event) {
      var t = event.target;

      if (t.closest('[data-open-new]')) return openOpeningModal(null);
      if (t.closest('[data-modal-close]')) return closeModals();

      var edit = t.closest('[data-edit]');
      if (edit) return openOpeningModal(edit.dataset.edit);

      var remove = t.closest('[data-remove]');
      if (remove) {
        var id = remove.dataset.remove;
        state.openings = state.openings.filter(function (o) { return o.id !== id; });
        if (activeId === id) activeId = state.openings.length ? state.openings[0].id : null;
        save(); renderAll();
        return;
      }

      var select = t.closest('[data-select]');
      if (select) { activeId = select.dataset.select; renderAll(); return; }

      var pick = t.closest('[data-pick]');
      if (pick) return openPicker(pick.dataset.pick, pick.dataset.for);

      var drop = t.closest('[data-drop]');
      if (drop) {
        var op = byId(drop.dataset.for);
        if (op) {
          var vid = parseInt(drop.dataset.drop, 10);
          op.extras = (op.extras || []).filter(function (e) { return e.variantId !== vid; });
          save(); renderAll();
        }
        return;
      }

      var step = t.closest('[data-step]');
      if (step) return bumpQty(step.dataset.for, step.dataset.target, parseInt(step.dataset.step, 10));

      var tab = t.closest('[data-tab]');
      if (tab && picker) {
        picker.tabIndex = parseInt(tab.dataset.tab, 10);
        els.pkSearch.value = '';
        var current = picker.tabs[picker.tabIndex];
        els.pkSearch.placeholder = 'Search ' + (current ? current.search : 'the catalogue') + '…';
        renderTabs();
        search(1);
        return;
      }

      var use = t.closest('[data-use]');
      if (use && picker) {
        var card = use.closest('[data-card]');
        var sel = card && card.querySelector('[data-variant]');
        if (sel) choose(use.dataset.use, parseInt(sel.value, 10));
        return;
      }

      var more = t.closest('[data-more]');
      if (more && picker) return search(parseInt(more.dataset.more, 10), true);

      if (t.closest('[data-pk-done]')) return closeModals();
    });

    root.addEventListener('change', function (event) {
      var other = event.target.closest && event.target.closest('select[data-other]');
      if (other) {
        syncOther(other);
        var box = otherInput(other);
        if (box && !box.hidden) box.focus({ preventScroll: true });
        return;
      }

      // Changing the variant dropdown inside a picker card repoints the displayed price.
      var sel = event.target.closest && event.target.closest('[data-variant]');
      if (!sel) return;
      var card = sel.closest('[data-card]');
      // NB: the <option> elements also carry data-price, so this must be the display's own
      // attribute or querySelector returns an option and rewrites the dropdown text.
      var price = card && card.querySelector('[data-price-display]');
      var opt = sel.options[sel.selectedIndex];
      if (price && opt && opt.dataset.price) {
        price.textContent = money(parseInt(opt.dataset.price, 10));
        delete price.dataset.d2fInc;   // let the VAT switch re-read the new figure
        if (window.d2fApplyVat) window.d2fApplyVat(card);
      }
    });

    root.addEventListener('input', function (event) {
      var t = event.target;

      if (t === els.pkSearch && picker) {
        clearTimeout(searchDebounce);
        searchDebounce = setTimeout(function () { search(1); }, 350);
        return;
      }

      if (t.hasAttribute && t.hasAttribute('data-qty')) {
        var next = Math.min(999, Math.max(1, parseInt(t.value, 10) || 1));
        // Correct the field too, or the number on screen disagrees with the price beside it.
        if (t.value !== '' && String(next) !== t.value) t.value = next;
        setQty(t.dataset.for, t.dataset.target, next);
      }
    });

    function bumpQty(openingId, target, delta) {
      var o = byId(openingId);
      if (!o) return;
      var current = target === 'door' ? o.qty : (o.extras || []).filter(function (e) { return String(e.variantId) === String(target); })[0];
      var value = target === 'door' ? o.qty : (current ? current.qty : 1);
      setQty(openingId, target, Math.min(999, Math.max(1, value + delta)));
    }

    function setQty(openingId, target, value) {
      var o = byId(openingId);
      if (!o) return;
      if (target === 'door') o.qty = value;
      else {
        var e = (o.extras || []).filter(function (x) { return String(x.variantId) === String(target); })[0];
        if (e) e.qty = value;
      }
      save();
      renderDetail(); renderFoot(); renderSchedule();
      if (window.d2fApplyVat) window.d2fApplyVat(root);
    }

    els.addAll.addEventListener('click', addAll);
    els.print.addEventListener('click', function () { window.print(); });
    els.share.addEventListener('click', shareLink);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && root.querySelector('.d2f-modal[open]')) closeModals();
    });

    /* ------------------------------------------------------------ start */

    function hashToken() {
      return (window.location.hash.match(/[#&]plan=([^&]+)/) || [])[1];
    }

    function loadShared(token) {
      var rows;
      try { rows = JSON.parse(unpack(token)); } catch (e) { return false; }
      if (!Array.isArray(rows)) return false;

      var existing = [];
      try { existing = JSON.parse(localStorage.getItem(STORE_KEY) || '[]'); } catch (e) { existing = []; }
      var worthKeeping = Array.isArray(existing) && existing.some(function (r) { return r && r.hd; });
      if (worthKeeping && !window.confirm('Open this shared plan? It will replace the plan you have saved on this device.')) {
        return false;
      }

      state.openings = expand(rows);
      activeId = state.openings.length ? state.openings[0].id : null;
      // Drop the token once read, or a later refresh replays the shared plan and quietly
      // throws away whatever the shopper changed after opening the link.
      try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (e) {}
      save();
      renderAll();
      hydrate();
      return true;
    }

    // A plan link opened while this page is already showing changes only the fragment, which
    // does not reload the page — without this, pasting a link here would appear to do nothing.
    window.addEventListener('hashchange', function () {
      var token = hashToken();
      if (!token) return;
      if (loadShared(token)) setStatus('Plan loaded from your link.');
      else setStatus('Sorry, that plan link could not be read.', true);
    });

    var token = hashToken();
    if (!token || !loadShared(token)) {
      var stored = [];
      try {
        stored = JSON.parse(localStorage.getItem(STORE_KEY) || localStorage.getItem(LEGACY_KEY) || '[]');
      } catch (e) { stored = []; }
      state.openings = expand(stored);
      activeId = state.openings.length ? state.openings[0].id : null;
      renderAll();
      hydrate();
    }
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var planners = root.querySelectorAll('[data-d2f-planner]');
    if (!planners.length && root.matches && root.matches('[data-d2f-planner]')) planners = [root];
    Array.prototype.forEach.call(planners, function (el) {
      if (el.dataset.d2fReady) return;
      el.dataset.d2fReady = '1';
      Planner(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // The theme editor replaces a section's markup wholesale; without this the planner looked
  // permanently broken to whoever was editing the page.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
